// components-dashboard/TopBar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsersStore } from '../store/useUsersStore';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';
import NotificationBell from '../components/NotificationBell';
import SwitchProfileModal from '../components/SwitchProfileModal';
import ChangeProfilePictureModal from '../components/ChangeProfilePictureModal';

const TopBar = ({ userName, useAiProposal = false, onToggleUseAiProposal }) => {
  const navigate = useNavigate();
  const { logout: fbLogout } = useFirebaseAuth();
  const users = useUsersStore((s) => s.users ?? []);
  const selectedKey = useUsersStore((s) => s.selectedKey ?? '');
  const selectedFromStore = useUsersStore((s) =>
    typeof s.getSelectedUser === 'function' ? s.getSelectedUser() : null
  );
  const selectUserAction = useUsersStore((s) => s.selectUser ?? null);

  const [open, setOpen] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [showProfilePicModal, setShowProfilePicModal] = useState(false);
  const [tempSelectedUser, setTempSelectedUser] = useState('');
  const [profileImage, setProfileImage] = useState('/Icons/boy.svg');
  const containerRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const handleToggle = () => setOpen((s) => !s);

  const handleSelectUser = (key) => {
    if (!key) return;
    const u = users.find((x) =>
      (x.sub_username && x.sub_username === key) ||
      String(x.id) === String(key) ||
      String(x.user_bid_id) === String(key)
    );
    if (typeof selectUserAction === 'function') {
      try {
        selectUserAction(key);
      } catch (e) {
        console.warn('selectUserAction failed', e);
        if (u) try { localStorage.setItem('SF_SELECTED_SUBUSER', JSON.stringify(u)); } catch {}
      }
    } else {
      if (u) try { localStorage.setItem('SF_SELECTED_SUBUSER', JSON.stringify(u)); } catch {}
    }
    setShowSwitchModal(false);
    setOpen(false);
  };

  const handleSwitchProfile = () => {
    setTempSelectedUser(String(selectedKey || ''));
    setShowSwitchModal(true);
    setOpen(false);
  };

  const handleConfirmSwitch = () => {
    if (tempSelectedUser) {
      handleSelectUser(tempSelectedUser);
    }
  };

  const handleChangeProfilePicture = () => {
    setShowProfilePicModal(true);
    setOpen(false);
  };

  const handleSaveProfilePicture = (file, previewUrl) => {
    console.log('Save profile picture:', file);
    setProfileImage(previewUrl);
  };

  const handleUserProfile = () => {
    navigate('/dashboard/update-profile');
    setOpen(false);
  };

  const handleNotifications = () => {
    console.log('Toggle notifications');
  };

  const handleUseAIProposalChange = (e) => {
    if (typeof onToggleUseAiProposal === 'function') {
      onToggleUseAiProposal(e.target.checked);
    }
  };

  const handleLogout = async () => {
    setOpen(false);
    if (typeof fbLogout === 'function') {
      try { await fbLogout(); } catch (e) { console.warn('Firebase logout failed', e); }
    }
  };

  const displayName =
    selectedFromStore?.sub_username ??
    selectedFromStore?.username ??
    selectedFromStore?.name ??
    selectedFromStore?.displayName ??
    userName ??
    'User';

  return (
    <>
      <header className="sticky top-0 z-50 bg-background-body border-b border-primary-black-shade-10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500"></div>

          <div className="flex items-center gap-4">
            <NotificationBell />

            {/* User Dropdown */}
            <div className="relative" ref={containerRef}>
              <button
                onClick={handleToggle}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                <img
                  src={profileImage}
                  alt={displayName}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div className="flex flex-col items-start">
                  <span className="text-xs text-gray-500">
                    Hi, <span className="font-semibold">{displayName}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-primary-blue">
                      {userName}
                    </span>
                    <img
                      src={open ? '/Icons/dropup-icon.svg' : '/Icons/dropdown-icon.svg'}
                      alt={open ? 'Collapse menu' : 'Expand menu'}
                      className="w-5 h-5 object-contain"
                    />
                  </div>
                </div>
              </button>

              {/* Dropdown Menu */}
              {open && (
                <div className="absolute right-0 mt-2 w-80 bg-white border-2 border-light-blue rounded-2xl shadow-xl z-50 py-4 px-4">
                  <div className="py-2">
                    {/* Switch Profile */}
                    <button
                      onClick={handleSwitchProfile}
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      Switch Profile
                    </button>

                    {/* Change Profile Picture */}
                    <button
                      onClick={handleChangeProfilePicture}
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      Change Profile Picture
                    </button>

                    {/* User Profile */}
                    <button
                      onClick={handleUserProfile}
                      className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      User Profile
                    </button>

                    {/* Notifications Toggle */}
                    <div className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors">
                      <span className="text-sm text-gray-700">Notifications</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          onChange={handleNotifications}
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-blue"></div>
                      </label>
                    </div>

                    {/* Use AI Proposal Toggle */}
                    <div className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors">
                      <span className="text-sm text-gray-700">Use AI Proposal For AutoBids</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={!!useAiProposal}
                          onChange={handleUseAIProposalChange}
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-blue"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Switch Profile Modal */}
      <SwitchProfileModal
        isOpen={showSwitchModal}
        onClose={() => setShowSwitchModal(false)}
        users={users}
        selectedKey={selectedKey}
        tempSelectedUser={tempSelectedUser}
        setTempSelectedUser={setTempSelectedUser}
        onConfirm={handleConfirmSwitch}
      />

      {/* Change Profile Picture Modal */}
      <ChangeProfilePictureModal
        isOpen={showProfilePicModal}
        onClose={() => setShowProfilePicModal(false)}
        currentImage={profileImage}
        onSave={handleSaveProfilePicture}
      />
    </>
  );
};

export default TopBar;
